import { useEffect, useState } from "react";
import api, { formatApiError, BACKEND_URL } from "../lib/api";
import { formatDateUS } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Copy, HandHeart, CheckCircle, Users, Coins, ChartLineUp, Share } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Referrals() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/referrals/me")
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <div className="p-12 text-slate-500 text-sm" data-testid="referrals-loading">Loading…</div>;
    }
    if (!data) {
        return <div className="p-12 text-slate-500 text-sm" data-testid="referrals-error">Could not load referrals.</div>;
    }

    const origin = (BACKEND_URL || window.location.origin).replace(/\/api$/, "");
    // Use the public marketing origin (frontend root), not the API root.
    const publicOrigin = window.location.origin;
    const refUrl = `${publicOrigin}/register?ref=${data.code}`;

    const copy = (text, what) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.success(`${what} copied`);
        }).catch(() => toast.error("Copy failed"));
    };

    const share = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: "LienLift Direct",
                    text: "Settle judgments & liens with AI-drafted letters and Stripe-paid offers.",
                    url: refUrl,
                });
            } catch (_) {
                /* user cancelled share — ignore */
            }
        } else {
            copy(refUrl, "Referral link");
        }
    };

    const pct = Math.round((data.commission_pct || 0) * 100);

    return (
        <div className="max-w-[1100px] mx-auto px-6 py-12" data-testid="referrals-page">
            <header className="flex items-center gap-3">
                <HandHeart size={26} weight="duotone" className="text-[#0F1E3D]" />
                <div>
                    <p className="overline">Referrals</p>
                    <h1 className="font-serif text-4xl mt-1.5 leading-tight">Earn {pct}% on every paid upgrade you bring in.</h1>
                </div>
            </header>
            <p className="text-slate-600 mt-4 max-w-2xl">
                Share your link with anyone — attorneys, accountants, debtors you&apos;ve settled with, friends. When they
                sign up and upgrade to Pro or Agency, you earn <strong>{pct}%</strong> of their first paid month, one-time.
            </p>

            <Card className="p-5 mt-8 flex items-center gap-3 flex-wrap" data-testid="referrals-link-card">
                <div className="flex-1 min-w-[260px]">
                    <p className="overline">Your referral link</p>
                    <Input value={refUrl} readOnly className="mt-2 h-11 font-mono text-sm bg-slate-50" data-testid="referral-url-input" />
                </div>
                <Button onClick={() => copy(refUrl, "Link")} className="h-11 bg-[#0F1E3D] hover:bg-[#1A2C57]" data-testid="copy-link-btn">
                    <Copy size={15} className="mr-1.5" /> Copy link
                </Button>
                <Button variant="outline" className="h-11" onClick={share} data-testid="share-link-btn">
                    <Share size={15} className="mr-1.5" /> Share
                </Button>
            </Card>

            <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                <span>Or share just the code:</span>
                <button
                    onClick={() => copy(data.code, "Code")}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-slate-200 font-mono text-slate-900 hover:bg-slate-50"
                    data-testid="copy-code-btn"
                >
                    {data.code} <Copy size={11} />
                </button>
            </div>

            <section className="mt-12">
                <p className="overline">Lifetime stats</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden mt-4">
                    <Stat icon={Share}       label="Clicks"             value={data.stats.clicks} testid="stat-clicks" />
                    <Stat icon={Users}       label="Signups"            value={data.stats.signups} testid="stat-signups" />
                    <Stat icon={ChartLineUp} label="Paid conversions"   value={data.stats.conversions} testid="stat-conversions" tint="blue" />
                    <Stat icon={Coins}       label="Total commission"   value={`$${data.stats.total_commission.toFixed(2)}`} testid="stat-total" tint="green" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden mt-3">
                    <Stat label="Pending payout" value={`$${data.stats.pending_commission.toFixed(2)}`} testid="stat-pending" tint="amber" />
                    <Stat label="Paid out"       value={`$${data.stats.paid_commission.toFixed(2)}`} testid="stat-paid" tint="green" />
                </div>
            </section>

            <section className="mt-12">
                <p className="overline">How it works</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <Step n={1} title="Share">Send your link or code. Every click is tracked.</Step>
                    <Step n={2} title="They sign up">We tag the new account as referred by you (lifetime attribution).</Step>
                    <Step n={3} title="You earn">When they upgrade to Pro ($49) or Agency ($99), you get {pct}% — one-time, on us.</Step>
                </div>
            </section>

            <section className="mt-12" data-testid="referrals-events">
                <div className="flex items-end justify-between flex-wrap gap-2">
                    <p className="overline">Recent activity</p>
                    <span className="text-xs text-slate-400">Most recent 20 events</span>
                </div>
                {data.recent_events.length === 0 ? (
                    <Card className="p-8 mt-4 text-center text-slate-500 text-sm" data-testid="events-empty">
                        No referrals yet — share your link to get the first click on the board.
                    </Card>
                ) : (
                    <div className="mt-4 border border-slate-200 rounded-md divide-y divide-slate-100">
                        {data.recent_events.map((e) => (
                            <div key={e.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`event-${e.id}`}>
                                <div className="flex items-center gap-3 min-w-0">
                                    {e.type === "tier_upgrade"
                                        ? <CheckCircle size={18} weight="fill" className="text-emerald-600 shrink-0" />
                                        : <Users size={18} weight="duotone" className="text-slate-500 shrink-0" />}
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-slate-900 truncate">
                                            {e.type === "tier_upgrade"
                                                ? <>Upgrade — <span className="capitalize">{e.tier}</span></>
                                                : "Signup"}
                                        </p>
                                        <p className="text-xs text-slate-500 truncate">
                                            {e.referred_email || "—"} · {formatDateUS(e.created_at)}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {e.commission_amount > 0 && (
                                        <span className="font-mono text-sm font-semibold text-emerald-700">
                                            +${e.commission_amount.toFixed(2)}
                                        </span>
                                    )}
                                    <Badge variant="outline" className="text-[10px] capitalize">{e.status}</Badge>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <p className="mt-12 text-xs text-slate-500 max-w-2xl">
                Payouts are processed manually each month. By referring, you agree that LienLift Direct may
                reverse any commission tied to a refunded, charged-back, or fraudulent upgrade.
            </p>
        </div>
    );
}

function Stat({ icon: Icon, label, value, tint, testid }) {
    const tones = {
        navy:  { bg: "bg-[#0F1E3D]", text: "text-white", lab: "text-white/60", ic: "text-white/60" },
        blue:  { bg: "bg-blue-50",   text: "text-blue-900",    lab: "text-blue-700",    ic: "text-blue-600" },
        green: { bg: "bg-emerald-50",text: "text-emerald-900", lab: "text-emerald-700", ic: "text-emerald-600" },
        amber: { bg: "bg-amber-50",  text: "text-amber-900",   lab: "text-amber-700",   ic: "text-amber-600" },
    };
    const t = tones[tint] || { bg: "bg-white", text: "text-slate-900", lab: "text-slate-500", ic: "text-slate-400" };
    return (
        <div className={`p-5 ${t.bg}`} data-testid={testid}>
            <div className="flex items-center gap-1.5">
                {Icon && <Icon size={13} className={t.ic} />}
                <p className={`overline ${t.lab}`}>{label}</p>
            </div>
            <p className={`font-mono text-3xl mt-2 font-semibold ${t.text}`}>{value}</p>
        </div>
    );
}

function Step({ n, title, children }) {
    return (
        <Card className="p-5">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0F1E3D] text-white text-xs font-mono">{n}</span>
            <h3 className="font-medium text-slate-900 mt-3">{title}</h3>
            <p className="text-sm text-slate-600 mt-1.5">{children}</p>
        </Card>
    );
}
